import React, { useCallback, useMemo, useState } from 'react';
import { useHistory } from 'react-router-dom';
import ReactTooltip from 'react-bootstrap/Tooltip';
import OverlayTrigger from 'react-bootstrap/OverlayTrigger';
import { Button } from 'primereact/button';
import { DataTable } from 'primereact/datatable';
import { Column } from 'primereact/column';
import {
	useCredentialByTypeQuery,
	useEnabledCredentialsQuery,
	useUploadCredentialMutation,
	useCredentialsForShiftTypeQuery
} from '../../../redux/external-agency';
import { PRIVATE_PATH } from '../../../constants';
import UploadGenericCredentialModal from '../../../components/modals/UploadCredentialModal/UploadGenericCredentialModal';
import UploadPLCredentialModal from '../../../components/modals/UploadCredentialModal/UploadPLCredentialModal';
import ShowCredentialsByFacilityModal from '../../../components/modals/ShowCredentialsByFacilityModal/ShowCredentialsByFacilityModal';
import uploadCredentialsIcon from '../../../assets/images/upload-file.svg';
import noCredentialsIcon from '../../../assets/images/scan_delete.svg';
import eyeIcon from '../../../assets/images/eye-purple.svg';
import arrowDownIcon from '../../../assets/images/arrow-down-purple-2.svg';
import infoIcon from '../../../assets/images/info-icon-black.svg';
import checkIcon from '../../../assets/images/check.svg';
import AgencyProfessionalsCredentialData from './AgencyProfessionalsCredentialData';
import AgencyProfessionalsCredentialActionButton from './AgencyProfessionalsCredentialActionButton';
import CredentialStatusPill from '../../../components/CredentialStatusPill/CredentialStatusPill';

import './AgencyProfessionalsCredentialList.scss';

const TypeHeader = () => (
	<div className="type-header">
		Type(s)
		<OverlayTrigger
			className="tooltip-component"
			placement="bottom-start"
			delay={{ show: 100, hide: 400 }}
			overlay={
				<ReactTooltip id="" className="pills-tooltip">
					<div className="tooltip-content">
						<span className="key-title">Key:</span>
						<div className="tooltip-pills-container">
							<CredentialStatusPill status="MISSING" title="Missing" />
							<CredentialStatusPill status="PENDING" title="Pending" />
							<CredentialStatusPill status="VERIFIED" title="Verified" />
							<CredentialStatusPill status="REJECTED" title="Rejected/Expired" />
						</div>
					</div>
				</ReactTooltip>
			}
		>
			<img src={infoIcon} alt="Ascending" className="info-icon" />
		</OverlayTrigger>
	</div>
);

const AgencyProfessionalsCredentialList = ({ caregiverId, primaryLicense }) => {
	const history = useHistory();

	const [expandedRows, setExpandedRows] = useState(null);
	const [credentialTypeCode, setCredentialTypeCode] = useState('');
	const [filterBtnText, setFilterBtnText] = useState('View credentials by facility');
	const [savedDropdownInfo, setSavedDropdownInfo] = useState({});
	const [showUploadGenericModal, setShowUploadGenericModal] = useState(false);
	const [showCredentialsByFacilityModal, setShowCredentialsByFacilityModal] = useState(false);
	const [showUploadPL, setShowUploadPL] = useState(false);

	const { data: credentialsForShiftType, isLoading: credentialsForShiftLoading } = useCredentialsForShiftTypeQuery(
		{
			caregiverId,
			facilityId: savedDropdownInfo?.facilityId,
			shiftType: savedDropdownInfo?.shiftType,
			specialtyType: savedDropdownInfo?.specialtyType
		},
		{
			skip: !savedDropdownInfo?.shiftType,
			refetchOnMountOrArgChange: true
		}
	);

	const { data: enabledCredentials, isLoading, error, isError } = useEnabledCredentialsQuery(caregiverId, {
		skip:
			savedDropdownInfo?.facilityId &&
			typeof credentialsForShiftType === 'object' &&
			Object.keys(credentialsForShiftType).length > 0,
		refetchOnMountOrArgChange: true
	});

	const notAccessible = useMemo(() => isError && error?.status === 403, [isError, error]);

	const onClosePLUploadModal = useCallback(() => {
		if (primaryLicense) {
			history.push(`${PRIVATE_PATH.PROFESSIONALS_PAGE}/${caregiverId}/employee-details`);
		} else {
			setShowUploadPL(false);
		}
	}, [primaryLicense]);

	const onUploadSuccess = () => setShowUploadPL(false);

	const statusTemplate = (item) => (
		<div className="status-container">
			{item.submitted.map((submittedSubtype) => (
				<CredentialStatusPill
					status={submittedSubtype.status}
					title={submittedSubtype.subtype_name}
					key={submittedSubtype.subtype_name}
				/>
			))}
		</div>
	);

	const licensesAndSpecialtiesTemplate = (item, columnData) => {
		const { field } = columnData;
		return <span className="licenses-and-specialties">{item[field] ? item[field] : '-'}</span>;
	};

	const rowExpansionTemplate = (rowData) => {
		return <AgencyProfessionalsCredentialData caregiverId={caregiverId} rowData={rowData} />;
	};

	const handleUpload = (credentialType) => {
		if (credentialType === 'PROFESSIONAL_LICENSE') {
			setShowUploadPL(true);
		} else {
			setShowUploadGenericModal(true);
			setCredentialTypeCode(credentialType);
		}
	};

	const actionsTemplate = (item) => {
		return AgencyProfessionalsCredentialActionButton({
			credentialType: item?.credential_type,
			onClickCallback: () => handleUpload(item?.credential_type),
			isUploadable: item?.isUploadable
		});
	};

	const neededToWorkTemplate = (item, columnData) => {
		const { field } = columnData;
		return item[field].requirementsMet ? <img src={checkIcon} alt="success" /> : item[field].neededToWork;
	};

	const expanderRowClass = (data) => {
		if ('isUploadable' in data && data.submitted?.some((elem) => elem.status !== 'MISSING')) {
			return data.isUploadable ? 'expandable' : 'default-expander';
		}
		if (data.submitted.length && data.submitted?.some((elem) => elem.status !== 'MISSING')) {
			return 'expandable';
		}
		return 'default-expander';
	};

	const onModalClose = (savedInfo, shiftTypeValue) => {
		if (savedInfo?.shiftType) {
			setSavedDropdownInfo(savedInfo);
			setExpandedRows(null);
		}
		if (shiftTypeValue) {
			setFilterBtnText(shiftTypeValue);
		}
		setShowCredentialsByFacilityModal(false);
	};

	const neededToWorkItems = useCallback((credential, verified, pending) => {
		if (credential.code === 'REFERENCES') {
			return {
				requirementsMet: verified.length >= credential.credentialLogic.requiredReferencesCount,
				neededToWork: credential.credentialLogic.requiredReferencesCount - verified.length
			};
		}

		if (credential.credentialLogic.logic === 'OR') {
			if (verified.length !== 0) {
				return {
					requirementsMet: true,
					neededToWork: -1
				};
			}

			if (pending.length !== 0) {
				return {
					requirementsMet: false,
					neededToWork: 0
				};
			}

			return {
				requirementsMet: false,
				neededToWork: 1
			};
		}

		// section for AND logic
		const exceptions = new Set(credential.credentialLogic.credentials?.filter((cred) => cred.exceptions_ok)).map(
			(cred) => cred.sub_type
		);

		// even if the credential doesn't have any subtypes we get an array with length 1 here.
		const credentialsWithoutExceptions = credential.subtypes?.filter((subtype) => !exceptions.has(subtype.sub_type));

		const isVerified = verified.length >= credentialsWithoutExceptions.length;

		return {
			requirementsMet: isVerified,
			neededToWork: isVerified ? -1 : credentialsWithoutExceptions.length - pending.length - verified.length
		};
	}, []);

	const getSubtypeName = (credential, code) => {
		return code === 'PROFESSIONAL_LICENSE'
			? credential.subtypeCode
			: credential.subtypeName ?? credential.credentialName;
	};

	const credentialData = useMemo(() => {
		if (savedDropdownInfo?.facilityId) {
			if (credentialsForShiftType && Object.keys(credentialsForShiftType).length > 0) {
				return credentialsForShiftType.map((credential) => {
					const pending = (credential.pending ?? []).map((pendingCredential) => ({
						status: pendingCredential.reason,
						subtype: pendingCredential.subtypeCode ?? pendingCredential.credentialCode,
						subtype_name: getSubtypeName(pendingCredential, credential.code)
					}));

					const missing = (credential.missing ?? []).map((missingCredential) => ({
						status: missingCredential.reason,
						subtype: missingCredential.subtypeCode ?? missingCredential.credentialCode,
						subtype_name: getSubtypeName(missingCredential, credential.code)
					}));

					const seenVerifiedSubtype = new Set();

					const verified = (credential.verified ?? [])
						.filter((verifiedCredential) => {
							const subtype = verifiedCredential.subtypeCode ?? verifiedCredential.credentialCode;
							// if we already added the subtype to the list of verified credentials
							// then don't add it again
							if (seenVerifiedSubtype.has(subtype)) {
								return false;
							}

							// add the subtype to the known list of subtypes so we don't add it multiple times.
							seenVerifiedSubtype.add(subtype);
							return true;
						})
						.map((verifiedCredential) => ({
							status: verifiedCredential.reason,
							subtype: verifiedCredential.subtypeCode ?? verifiedCredential.credentialCode,
							subtype_name: getSubtypeName(verifiedCredential, credential.code)
						}));

					const neededToWork = neededToWorkItems(credential, verified, pending);

					return {
						credential_type: credential.code,
						name: credential.name,
						submitted: [...missing, ...pending, ...verified],
						neededToWork,
						isUploadable: credential.is_uploadable
					};
				});
			}
			return [];
		}
		return enabledCredentials;
	}, [enabledCredentials, credentialsForShiftType, savedDropdownInfo?.facilityId]);

	const clearFilter = () => {
		setFilterBtnText('View credentials by facility');
		setSavedDropdownInfo({});
		setExpandedRows(null);
	};

	const buildAllowedPLSubtypes = () => {
		if (savedDropdownInfo?.facilityId) {
			const allowedPL = credentialsForShiftType.find((cred) => cred.code === 'PROFESSIONAL_LICENSE');
			return allowedPL.subtypes.reduce((acc, subtype) => {
				if (subtype.sub_type && !subtype.exceptions_ok) {
					acc.set(subtype.sub_type, subtype.sub_name);
				}
				return acc;
			}, new Map());
		}
		return new Map();
	};

	const conditionForNoCredentialRequirements =
		!credentialsForShiftLoading && credentialData && credentialData.length === 0 && savedDropdownInfo.facilityName;

	return (
		<>
			<div className="toolbar">
				<Button
					disabled={notAccessible || conditionForNoCredentialRequirements}
					className="btn upload-button btn-light-purple"
					onClick={() => setShowUploadGenericModal(true)}
				>
					<img src={uploadCredentialsIcon} alt="Upload Credentials" />
					<div className="button-label">Upload Credentials</div>
				</Button>
				<div className="right-section">
					{Object.keys(savedDropdownInfo).length > 0 && (
						<div
							className="clear-filter"
							role="button"
							aria-hidden="true"
							onClick={() => clearFilter()}
							onKeyDown={() => clearFilter()}
						>
							Clear filter
						</div>
					)}
					<Button
						className="btn upload-button btn-light-purple"
						disabled={notAccessible}
						onClick={() => setShowCredentialsByFacilityModal(true)}
					>
						{savedDropdownInfo?.facilityId ? <img src={arrowDownIcon} alt="" /> : <img src={eyeIcon} alt="" />}
						<div className="button-label">{filterBtnText}</div>
					</Button>
				</div>
			</div>

			{conditionForNoCredentialRequirements && (
				<div className="no-credentials">
					<img src={noCredentialsIcon} alt="No Credentials" />
					<div className="no-credentials-text">
						There are currently no credentials required to work{' '}
						{filterBtnText.substring(filterBtnText.indexOf('-') + 1)} shifts at {savedDropdownInfo.facilityName}
					</div>
				</div>
			)}

			{credentialData && credentialData.length >= 1 && (
				<DataTable
					value={credentialData}
					scrollHeight="flex"
					sortMode="multiple"
					virtualScrollerOptions={{ itemSize: 65 }}
					tableStyle={{ tableLayout: 'fixed' }}
					size="small"
					stripedRows
					loading={isLoading}
					removableSort
					rowExpansionTemplate={rowExpansionTemplate}
					expandedRows={expandedRows}
					onRowToggle={(row) => setExpandedRows(row.data)}
					dataKey="credential_type"
				>
					<Column expander bodyClassName={expanderRowClass} style={{ width: '1%', textAlign: 'center' }} />
					<Column
						field="name"
						header="Credential"
						style={{ width: '10%', padding: '1.6rem  1.6rem 1.6rem 0rem' }}
						sortable
						body={licensesAndSpecialtiesTemplate}
					/>
					{savedDropdownInfo && savedDropdownInfo.facilityName && (
						<Column
							field="neededToWork"
							header="Needed to work"
							style={{ width: '5%', padding: '1.6rem  1.6rem 1.6rem 0rem' }}
							sortable
							sortField="neededToWork.neededToWork"
							body={neededToWorkTemplate}
						/>
					)}
					<Column
						field="submitted"
						header=<TypeHeader />
						style={{ width: '20%', padding: '0.8rem 1.6rem' }}
						sortable
						body={statusTemplate}
					/>
					<Column
						field="actions"
						header="Actions"
						style={{ width: '3%', padding: '0.8rem 1.6rem' }}
						alignFrozen="right"
						body={actionsTemplate}
					/>
				</DataTable>
			)}

			{showUploadGenericModal && (
				<UploadGenericCredentialModal
					credentialType={credentialTypeCode}
					caregiverId={caregiverId}
					enabledCredentials={enabledCredentials}
					uploadCredentialMutation={useUploadCredentialMutation}
					credentialByTypeQuery={useCredentialByTypeQuery}
					onClose={() => {
						setShowUploadGenericModal(false);
						setCredentialTypeCode('');
					}}
					allowedCredentials={savedDropdownInfo?.facilityId ? credentialsForShiftType : undefined}
					showPLInfo
				/>
			)}

			{showCredentialsByFacilityModal && (
				<ShowCredentialsByFacilityModal
					onClose={(savedInfo, shiftTypeValue) => onModalClose(savedInfo, shiftTypeValue)}
					selectedValue={filterBtnText !== 'View credentials by facility' ? filterBtnText : ''}
				/>
			)}

			{(showUploadPL || primaryLicense) && (
				<UploadPLCredentialModal
					caregiverId={caregiverId}
					primaryLicense={primaryLicense}
					uploadCredentialMutation={useUploadCredentialMutation}
					credentialByTypeQuery={useCredentialByTypeQuery}
					allowedPLsubtypes={buildAllowedPLSubtypes()}
					onClose={onClosePLUploadModal}
					onSuccess={onUploadSuccess}
				/>
			)}
		</>
	);
};

export default AgencyProfessionalsCredentialList;
